import { Injectable } from '@angular/core';
import {BehaviorSubject, catchError, Observable, of, switchMap, throwError} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {map, shareReplay, tap} from 'rxjs/operators';
import {ToastService} from './toast.service';
import {Router} from '@angular/router';
import {LocalStorageService} from './local-storage.service';
import { WebSocketService } from './web-socket.service';

interface UserData {
  name: string;
  role: number;
  uuid: string;
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // https://valor-software.com/articles/json-web-token-authorization-with-access-and-refresh-tokens-in-angular-application-with-node-js-server.html
  public user$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public refreshToken$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private http: HttpClient,
    private toast: ToastService,
    private router: Router,
    private localStorageService: LocalStorageService,
    // private webSocketService: WebSocketService
  ) {}

  public get currentUserValue() {
    return this.user$.value;
  }

  public getCurrentUser() {
    return this.user$.pipe(switchMap((user: any) => {
      if (user) {
        return of(user);
      }
      const token = this.localStorageService.getItem('accessToken');
      if (token) {
        return this.fetchCurrentUser();
      }
      return of(null);
    }))
  }

  public fetchCurrentUser(): Observable<any> {
    return this.http.get(environment.API_PATH + '/user').pipe(tap((user: any) => {
      const loggedUser = this.localStorageService.getItem('user');
      if (!loggedUser || loggedUser._id !== user._id) {
        this.logout(true);
      } else {
        this.localStorageService.setItem('user', user);
        this.user$.next(user);
      }
    }));
  }

  public login(email: string, password: string): Observable<any> {
    return this.http.post(environment.API_PATH + '/user/login', {email, password})
      .pipe(tap((resp: any) => {
      if (resp && resp.accessToken) {
        this.setToken('accessToken', resp.accessToken);
        this.setToken('refreshToken', resp.refreshToken);
        this.setToken('user', resp.user);
        this.user$.next(resp.user);
      }
      return resp;
    })).pipe(catchError((err: any) => {
      this.toast.error(err.error.message, 'Login', {
        closeButton: true,
        timeOut: 6000,
        extendedTimeOut: 3000,
      });
      return throwError(err);
    }))
  }

  public refreshToken() {
    const refreshToken = this.localStorageService.getItem('refreshToken');
    return this.http.post(environment.API_PATH + '/user/refreshToken', {
      refreshToken,
    }).pipe(tap((resp: any) => {
      this.setToken('accessToken', resp.accessToken);
      this.setToken('refreshToken', resp.refreshToken);
      // update ws connection?
      this.refreshToken$.next(1);
    }));
  }

  private setToken(key: string, token: string): void {
    this.localStorageService.setItem(key, token);
  }

  public logout(reload: boolean = false) {
    // need post server to revoke-token
    this.localStorageService.removeItem("accessToken");
    this.localStorageService.removeItem("refreshToken");
    this.localStorageService.removeItem("user");
    this.localStorageService.removeItem("users");
    this.localStorageService.removeItem("brands");
    this.localStorageService.removeItem("types");
    this.user$.next(null);
    this.router.navigateByUrl('/login');

    if (reload) {
      // window.location.reload();
    }
  }
}
